import React, { Fragment, FC } from 'react';
import Head from 'next/head';
import { appPage } from 'src/browser';
import { Global, css } from '@emotion/core';
import frontendAppConfig from '../server/config/frontend-app.config';
import useMakeZapierRequestsRelative from '../browser/hooks/useMakeZapierRequestsRelative';
import useDataDogRum from '../browser/hooks/useDataDogRum';
import useBaseConfig from '../browser/hooks/useBaseConfig';

const globalStyles = css`
  html {
    min-height: 100%;
    background-color: #f2f2f2;
    font-family: 'Open Sans', Helvetica, arial, sans-serif;
  }
  /* Enforce vertical height */
  body,
  body > *:first-of-type,
  body > *:first-of-type > *:first-of-type,
  body > *:first-of-type > *:first-of-type > *:first-of-type {
    min-height: 100vh;
  }
  body {
    color: #666666;
  }

  * {
    font-family: inherit;
  }
  button {
    border: 1px solid #d6d6d6;
  }
  .row {
    text-align: center;
  }

  p {
    line-height: 1.6em;
    margin: 0 0 15px;
  }

  ol {
    line-height: 1.75em;
    list-style-type: decimal;
    list-style-position: inside;
    margin: 0 0 20px;
    padding: 0;
  }

  .img128,
  .img64,
  .img32,
  .img16 {
    background-position: center center;
    background-repeat: no-repeat;
    display: inline-block;
    vertical-align: middle;
  }
  .img128 {
    width: 128px;
    height: 128px;
    background-size: 64px 64px;
  }
  .img64 {
    width: 64px;
    height: 64px;
    background-size: 64px 64px;
  }
  .img32 {
    width: 32px;
    height: 32px;
    background-size: 32px 32px;
  }
  .img16 {
    width: 16px;
    height: 16px;
    background-size: 16px 16px;
  }
  .img16.placeholder,
  .PlaceholderAPI16x16 {
    background-image: url('https://cdn.zapier.com/zapier/images/frontend/placeholder/16x16.grey.png');
    background-position: center center;
    background-repeat: no-repeat;
  }
  .img32.placeholder,
  .PlaceholderAPI32x32 {
    background-image: url('https://cdn.zapier.com/zapier/images/frontend/placeholder/32x32.grey.png');
    background-position: center center;
    background-repeat: no-repeat;
  }
  .img64.placeholder,
  .PlaceholderAPI64x64 {
    background-image: url('https://cdn.zapier.com/zapier/images/frontend/placeholder/64x64.grey.png');
    background-position: center center;
    background-repeat: no-repeat;
  }
  .img128.placeholder,
  .PlaceholderAPI128x128 {
    background-image: url('https://cdn.zapier.com/zapier/images/frontend/placeholder/128x128.grey.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .GoogleSheetsAPI16x16,
  .GoogleSheetsAPI32x32,
  .GoogleSheetsAPI64x64,
  .GoogleSheetsAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/e37713ab22d82f777575279db97cd805.128x128.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .MailChimpAPI16x16,
  .MailChimpAPI32x32,
  .MailChimpAPI64x64,
  .MailChimpAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/5c727288d9c2f69a9eee136c5f5a0f72.128x128.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .PivotalTrackerAPI16x16,
  .PivotalTrackerAPI32x32,
  .PivotalTrackerAPI64x64,
  .PivotalTrackerAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/84172dde558de382873cf72a00c92818.128x128.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .SurveyMonkeyAPI16x16,
  .SurveyMonkeyAPI32x32,
  .SurveyMonkeyAPI64x64,
  .SurveyMonkeyAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/39b027caffb7fad05f73ed80d664a246.128x128.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .GoogleContactsV2API16x16,
  .GoogleContactsV2API32x32,
  .GoogleContactsV2API64x64,
  .GoogleContactsV2API128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/1508661b55cd3c5ad1787303b0f58c99.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .SkyDriveAPI16x16,
  .SkyDriveAPI32x32,
  .SkyDriveAPI64x64,
  .SkyDriveAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/e67867e9f6f5c089e243dad54467ad33.128x128.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .EventbriteAPI16x16,
  .EventbriteAPI32x32,
  .EventbriteAPI64x64,
  .EventbriteAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/photos/4b2b5f32f92c80fc304ab369be1da347.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .BranchingAPI16x16,
  .BranchingAPI32x32,
  .BranchingAPI64x64,
  .BranchingAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/137baf61724ea2c64812417382bddd1d.128x128.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .ZapierInternalAPI16x16,
  .ZapierInternalAPI32x32,
  .ZapierInternalAPI64x64,
  .ZapierInternalAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/photos/ff7409c6a52dc217fe9829531c3a66dd.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .AppTesterAPI16x16,
  .AppTesterAPI32x32,
  .AppTesterAPI64x64,
  .AppTesterAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/4401a6c0fdab0b69893ab957f2712b81.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .OneNoteDevAPI16x16,
  .OneNoteDevAPI32x32,
  .OneNoteDevAPI64x64,
  .OneNoteDevAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/developer/e951e0105790e43b117e04b7bba34601.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .EventbriteDevAPI16x16,
  .EventbriteDevAPI32x32,
  .EventbriteDevAPI64x64,
  .EventbriteDevAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/developer/638ebef07f1e312e20ee45ddb7df6be5.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .TypeformDevAPI16x16,
  .TypeformDevAPI32x32,
  .TypeformDevAPI64x64,
  .TypeformDevAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/developer/6df0e2a9ca7350c7407f0d9c2b0709c0.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .QuickBaseAPI16x16,
  .QuickBaseAPI32x32,
  .QuickBaseAPI64x64,
  .QuickBaseAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/3d348ba36ddb1a656e434ccbb984e3c6.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .CapsuleCRMAPI16x16,
  .CapsuleCRMAPI32x32,
  .CapsuleCRMAPI64x64,
  .CapsuleCRMAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/1029d32eb05260b4a52ba6eebf5dc20f.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .ZohoCRMAPI16x16,
  .ZohoCRMAPI32x32,
  .ZohoCRMAPI64x64,
  .ZohoCRMAPI128x128 {
    background-image: url('https://images.zapier.com/storage/services/58719c2f59576a993b6c6855e0f585df.128x128.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .GoogleMailAPI16x16,
  .GoogleMailAPI32x32,
  .GoogleMailAPI64x64,
  .GoogleMailAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/0f5a0d7ee18ced262570a8c831e1a04a.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .SalesforceAPI16x16,
  .SalesforceAPI32x32,
  .SalesforceAPI64x64,
  .SalesforceAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/e18d02bc3d9fdcf0d4bf9ad43d8a8e6d.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .ScheduleAPI16x16,
  .ScheduleAPI32x32,
  .ScheduleAPI64x64,
  .ScheduleAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/services/9abbda159e8d9cec0e5eaa8ff1fd5205.png');
    background-position: center center;
    background-repeat: no-repeat;
  }

  .ChargifyAPI16x16,
  .ChargifyAPI32x32,
  .ChargifyAPI64x64,
  .ChargifyAPI128x128 {
    background-image: url('https://cdn.zapier.com/storage/developer_cli/a35dd2701c36c8d4a05610011061be7f_2.png');
    background-position: center center;
    background-repeat: no-repeat;
  }
`;

// This component proves the ZDL, and passes the stepId
// to child components. That's it!
// Don't let this become a god component! (See Dt 5:6)
const App: FC<any> = ({ Component, pageProps }) => {
  useMakeZapierRequestsRelative();
  useDataDogRum();
  useBaseConfig();

  return (
    <Fragment>
      <Global styles={globalStyles} />
      <Head>
        {/**
         * Viewport tags aren't deduped in _document.js, so it needs to be defined here
         * See: https://github.com/vercel/next.js/blob/master/errors/no-document-viewport-meta.md
         */}
        <meta content="width=device-width, initial-scale=1.0" name="viewport" />
      </Head>
      <Component {...pageProps} />
      {/**
       * This is a bit of a hack to prevent private logos from blocking rendering.
       * Using rel="preload" loads the CSS asynchronously. Once this has downloaded,
       * we switch the rel to "stylesheet", so that the browser actually uses the styles
       */}
      <link
        as="style"
        href="/generated/private-logos.css"
        onLoad={({ currentTarget }) => {
          currentTarget.rel = 'stylesheet';
        }}
        rel="preload"
      />
      <link href="/generated/global-logos.css" rel="stylesheet" />
    </Fragment>
  );
};

export default appPage(App, frontendAppConfig);
